import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styles from './styles.module.scss';
import Button from '@components-clean/core/button';
import DragDropListsCore from '@components-clean/core/drag-drop-lists-core';
import SimpleDragDropItem from '@components-clean/core/drag-drop-lists-core/drag-drop-item-simple';
import { DragDropListItemProps } from '@components-clean/core/drag-drop-lists-core/drag-drop-lists-core.interface';
import EditCard from '@components-clean/core/edit-card';
import ErrorMessage from '@components-clean/core/error';
import Loading from '@components-clean/core/loading/loading';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import {
    DataViewAssociationDialog,
    PanelDataViews,
} from '@pages-clean/admin/study/manage-hubs/central-monitoring/controls-hub-panels-config/data-view-association-dialog';
import { PanelType } from 'types/graphql-global-types';
import { useTranslation } from '@hooks/index';
import { ShowError, ShowSuccess } from '@utils/notification';
import { ADD_CONTROLS_HUB_CONFIG_PANELS, GET_CONTROLS_HUB_CONFIG_PANELS } from '@queries/central-monitoring';
import {
    getPanels_OpraQueries_panels_Panel,
    getControlsHubConfigPanelsForStudy,
    getControlsHubConfigPanelsForStudy_OpraQueries_getControlsHubConfigPanelsForStudy_ControlsHubConfigPanels_panels_PanelAndDataView,
    getControlsHubConfigPanelsForStudyVariables,
    addControlsHubConfigPanels,
    addControlsHubConfigPanelsVariables,
} from '@queries/generated/central-monitoring.generated';

type Props = {
    orgKey: string;
    studyRef: string;
    canEdit: boolean;
    panelData: (getPanels_OpraQueries_panels_Panel | null)[] | null;
};

type ConfigPanelsStateProps = {
    display: DragDropListItemProps[];
    available: DragDropListItemProps[];
};

type PanelWithDataView =
    getControlsHubConfigPanelsForStudy_OpraQueries_getControlsHubConfigPanelsForStudy_ControlsHubConfigPanels_panels_PanelAndDataView;

const COMPONENT_NAME = 'controlsHubPanelsConfig';

export const ControlsHubPanelsConfig = ({ orgKey, studyRef, canEdit, panelData }: Props) => {
    const { confirmDiscardChanges, setComponentHasChanges } = useUnsavedChangesContext();

    const { t } = useTranslation();
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [showDataViewsAssociation, setShowDataViewsAssociation] = useState<boolean>(false);

    const [saveConfig, { loading: isLoading, error: isError }] = useMutation<
        addControlsHubConfigPanels,
        addControlsHubConfigPanelsVariables
    >(ADD_CONTROLS_HUB_CONFIG_PANELS);

    const [getControlsHubConfigPanels, { data: controlsHubConfigPanels }] = useLazyQuery<
        getControlsHubConfigPanelsForStudy,
        getControlsHubConfigPanelsForStudyVariables
    >(GET_CONTROLS_HUB_CONFIG_PANELS);

    useEffect(() => {
        getControlsHubConfigPanels({ variables: { studyId: studyRef } }).catch((err) => window.console.error(err));
    }, [orgKey, studyRef]);

    const selectedPanelRefs = useMemo(() => {
        if (!controlsHubConfigPanels?.getControlsHubConfigPanelsForStudy?.panels) return [];

        return controlsHubConfigPanels?.getControlsHubConfigPanelsForStudy?.panels.filter(
            (panel): panel is PanelWithDataView => !!panel,
        );
    }, [controlsHubConfigPanels]);

    const allPanels: DragDropListItemProps[] = useMemo(() => {
        if (!panelData) return [];

        const validPanelTypes = [PanelType.EVENT_RATES_OVER_TIME, PanelType.PROPORTIONS_OVER_TIME];

        return panelData
            .filter(
                (panel): panel is getPanels_OpraQueries_panels_Panel =>
                    !!(panel && panel.panelType && validPanelTypes.includes(panel.panelType)),
            )
            .map((panel) => ({
                reference: panel.id! as string,
                title: panel.graph?.plotTitle ?? '[Title Missing]',
                active: panel.active ?? false,
            }));
    }, [panelData]);

    const [currentState, setCurrentState] = useState<ConfigPanelsStateProps>({
        display: [],
        available: [],
    });

    const initialState = useRef<ConfigPanelsStateProps>({
        display: [],
        available: [],
    });

    const reorderPanels = (array: DragDropListItemProps[], configOrder: PanelWithDataView[]) => {
        array.sort((a, b) => {
            if (
                configOrder.findIndex((p) => p.panelRef === b.reference) <
                configOrder.findIndex((p) => p.panelRef === a.reference)
            ) {
                return 1;
            }
            return -1;
        });
    };

    useEffect(() => {
        const result = allPanels.reduce<{
            available: DragDropListItemProps[];
            selected: DragDropListItemProps[];
        }>(
            (prev, curr) => {
                const selectedPanel = selectedPanelRefs.find((x) => x.panelRef === (curr.reference as string));
                if (selectedPanel) prev.selected.push({ ...curr, dataViewRef: selectedPanel.dataViewRef });
                else if (curr.active) prev.available.push({ ...curr, dataViewRef: null });

                return prev;
            },
            {
                available: [],
                selected: [],
            },
        );

        reorderPanels(result.selected, selectedPanelRefs);

        setCurrentState({
            display: result.selected,
            available: result.available,
        });

        initialState.current = {
            display: result.selected,
            available: result.available,
        };
        setIsEditMode(false);
    }, [panelData, selectedPanelRefs]);

    const hasChanges = useMemo(() => {
        return (
            JSON.stringify(
                currentState.display.map((w) => ({
                    reference: w.reference,
                    dataView: w.dataViewRef ?? null,
                })),
            ) !==
            JSON.stringify(
                initialState.current.display.map((w) => ({
                    reference: w.reference,
                    dataView: w.dataViewRef ?? null,
                })),
            )
        );
    }, [currentState.display, initialState.current.display]);

    const onEdit = () => {
        setIsEditMode(true);
    };

    const onCancel = () => {
        // Set initial values to current state
        setCurrentState({
            display: initialState.current.display,
            available: initialState.current.available,
        });
        setIsEditMode(false);
    };

    const onSave = () => {
        const selectedPanels = currentState.display.map((p) => {
            return {
                panelRef: p.reference as string,
                dataViewRef: p.dataViewRef as string | undefined,
            };
        });

        saveConfig({
            variables: {
                studyId: studyRef,
                panels: selectedPanels,
            },
        })
            .then((response) => {
                if (!response?.data?.addControlsHubConfigPanels) {
                    ShowError(t('errorMessage'));
                    return;
                }

                setIsEditMode(false);
                setComponentHasChanges(COMPONENT_NAME, false);
                ShowSuccess(t('actionsSuccessfulToast'));
            })
            .catch(() => {
                ShowError(t('errorMessage'));
            });
    };

    const onEditCardCancel = async () => {
        if (hasChanges) {
            const ok = await confirmDiscardChanges('confirmation-dialog');
            if (!ok) {
                return;
            }
            onCancel();
        } else {
            onCancel();
        }
    };

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, hasChanges);
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
    }, [hasChanges]);

    const onDataViewsClick = () => {
        setShowDataViewsAssociation(true);
    };

    return (
        <>
            {showDataViewsAssociation && (
                <DataViewAssociationDialog
                    studyRef={studyRef}
                    onClose={() => setShowDataViewsAssociation(false)}
                    onSubmit={(data) => {
                        setCurrentState((curr) => ({
                            ...curr,
                            display: data,
                        }));
                        setShowDataViewsAssociation(false);
                    }}
                    panels={currentState.display as PanelDataViews[]}
                />
            )}
            <EditCard
                title={t('cm:panels')}
                onEdit={onEdit}
                onCancel={onEditCardCancel}
                onSave={onSave}
                isEditMode={isEditMode}
                disabled={isLoading || !canEdit}
                data-testid="panels-config"
                saveDisabled={!hasChanges}
            >
                {isLoading && !isError && <Loading />}
                {isError && <ErrorMessage />}
                {!isLoading && !isError && (
                    <div style={{ height: '417px' }}>
                        <DragDropListsCore
                            list1Header={t('study:panelsDisplay')}
                            list2Header={t('study:availablePanels')}
                            list1Items={currentState.display}
                            list2Items={currentState.available}
                            isTwoLists
                            idField="reference"
                            onChangeItemsLeft={(display) => {
                                setCurrentState((curr) => {
                                    return {
                                        ...curr,
                                        display,
                                    };
                                });
                            }}
                            onChangeItemsRight={(available) => {
                                setCurrentState((curr) => {
                                    return {
                                        ...curr,
                                        available: available.map((x) => {
                                            delete x.dataViewRef;
                                            return x;
                                        }),
                                    };
                                });
                            }}
                            disabled={!isEditMode}
                            className="k-mb-4"
                            list1HeaderActions={
                                isEditMode && (
                                    <Button
                                        data-testid="data-view-association"
                                        primary
                                        disabled={!currentState.display.length}
                                        faIcon={['fas', 'link']}
                                        faIconStyle={{
                                            height: '12px',
                                            width: '13.5px',
                                        }}
                                        onClick={onDataViewsClick}
                                    >
                                        {t('cm:dataViews')}
                                    </Button>
                                )
                            }
                            renderItemLeft={({ dataItem, ...rest }) => {
                                let suffix: ReactNode | undefined;
                                const showSuffix = !!dataItem?.dataViewRef;
                                if (showSuffix) {
                                    suffix = (
                                        <FontAwesomeIcon className={styles.associationSuffix} icon={['fas', 'link']} />
                                    );
                                }

                                return (
                                    <SimpleDragDropItem {...rest} dataItem={{ ...dataItem, suffix }} toDisplayColumn />
                                );
                            }}
                        />
                    </div>
                )}
            </EditCard>
        </>
    );
};
